import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Box, Typography, useMediaQuery, InputBase } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import AssuredWorkloadIcon from "@mui/icons-material/AssuredWorkload";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SummarizeIcon from "@mui/icons-material/Summarize";
import BarChartIcon from "@mui/icons-material/BarChart";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import TimelineIcon from "@mui/icons-material/Timeline";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import DescriptionIcon from "@mui/icons-material/Description";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InventoryIcon from "@mui/icons-material/Inventory";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import GstIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import PieChartIcon from "@mui/icons-material/PieChart";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import HandymanIcon from "@mui/icons-material/Handyman";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { useAuth } from "../../../AuthContext";
import useAuditLog, { AUDIT_ACTION } from "../../../hooks/useAuditLog";
import { fetchData, postData, deleteData } from "../../../apiService";

const reportGroups = [

  // ─────────────────────────────────────────────────────────────
  // 1. FINANCIAL STATEMENTS
  //    MIS Reports + Journal / Day Books + GST + Assets
  // ─────────────────────────────────────────────────────────────
  {
    label: "Financial Statements",
    groupIcon: AccountBalanceIcon,
    color: "#263078",
    bg: "#e8eaf6",
    reports: [
      // MIS
      { label: "MIS Reports", route: null, icon: AssessmentIcon, isSection: true },
      { label: "1. Trial Balance", route: "/TrialBalanceReport", icon: BarChartIcon },
      { label: "2. Trial Balance Advanced", route: "/TrialBalanceAdvancedReport", icon: BarChartIcon },
      { label: "3. Ratio Analysis", route: "/RatioAnalysisReport", icon: BarChartIcon },
      { label: "4. Profit & Loss Statement", route: "/ProfitLossMonthWise", icon: BarChartIcon },
      { label: "5. Balance Sheet", route: "/BalanceSheet", icon: AccountBalanceWalletIcon },
      // { label: "6. Balance Sheet TView", route: "/BalanceSheetTView", icon: AccountBalanceWalletIcon },
      { label: "6. Cash Flow Statement", route: "/CashFlowStatement", icon: TimelineIcon },
      { label: "7. Fund Flow Statement", route: "/FundFlowStatement", icon: TimelineIcon },
      { label: "8. Scheme Summary", route: "/SchemeSummaryReport", icon: DescriptionIcon },
      { label: "9. Daily Profit Report", route: "/DailyProfitReport", icon: BarChartIcon },
      { label: "10. Staff Target Report", route: "/StaffTargetReport", icon: TrendingUpIcon },
      { label: "11. Stock Branch Wise Report", route: "/StockBranchWiseReport", icon: InventoryIcon },
      { label: "12. COD Payment Status", route: "/CashOnDeliveryPaymentStatusReport", icon: ReceiptLongIcon },
      // Journal / Day Books
      { label: "Journal & Day Books", route: null, icon: MenuBookIcon, isSection: true },
      { label: "1. Delayed Payment", route: "/DelayedPaymentReport", icon: AssuredWorkloadIcon },
      { label: "2. Delayed Collection", route: "/DelayedCollectionReport", icon: AssuredWorkloadIcon },
      { label: "3. Collections (Received)", route: "/CollectionReport", icon: AssuredWorkloadIcon },
      { label: "4. Payments (Paid)", route: "/PaymentsReport", icon: AssuredWorkloadIcon },
      { label: "5. Journal Books", route: "/JournalBookReport", icon: MenuBookIcon },
      { label: "6. Bank Receipt Journal Book", route: "/BankReceiptJournalReport", icon: MenuBookIcon },
      { label: "7. Bank Payment Journal Book", route: "/BankPaymentJournalReport", icon: AccountBalanceWalletIcon },
      { label: "8. Sales Journal Book", route: "/SaleJournalReport", icon: DescriptionIcon },
      { label: "9. Purchase Journal Book", route: "/PurchaseJournalReport", icon: AssuredWorkloadIcon },
      { label: "10. Day Book", route: "/DayBookReport", icon: MenuBookIcon },
      { label: "11. Day Book Summary", route: "/DayBookSummaryReport", icon: ReceiptLongIcon },
      { label: "12. Bank Book", route: "/BankBookReport", icon: RequestPageIcon },
      // GST Reports
      { label: "GST Returns", route: null, icon: GstIcon, isSection: true },
      { label: "1. GSTR 1", route: "/SaleGstReport", icon: MenuBookIcon },
      { label: "2. GSTR 3B", route: "/GSTR3BReport", icon: AssuredWorkloadIcon },
      { label: "3. GSTR 3B Format", route: "/GSTR3BFormatReport", icon: AssuredWorkloadIcon },
      { label: "GST Other Reports", route: null, icon: AssessmentIcon, isSection: true },
      { label: "1. Tax Register Sale", route: "/TaxRegisterSale", icon: SummarizeIcon },
      { label: "2. Tax Register Purchase", route: "/TaxRegisterPurchase", icon: CompareArrowsIcon },
      { label: "3. HSN-Tax Wise Summary", route: "/HSNTaxSummary", icon: SummarizeIcon },
      { label: "4. Purchase HSN-Wise", route: "/PurchaseHSNSummary", icon: CompareArrowsIcon },
      { label: "5. HSN Summary", route: "/HSNSummary", icon: SummarizeIcon },
      // Assets
      { label: "Assets", route: null, icon: HandymanIcon, isSection: true },
      { label: "1. Asset Registration", route: "/AssetRegistration", icon: CompareArrowsIcon },
      { label: "2. Asset Depreciation Registration (FinYear)", route: "/AssetDepreciation", icon: TimelineIcon },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  // 2. ACCOUNTS
  //    Ledger / CashBook + Inventory Books + Stock Valuation
  // ─────────────────────────────────────────────────────────────
  {
    label: "Accounts",
    groupIcon: AccountBalanceWalletIcon,
    color: "#4e342e",
    bg: "#efebe9",
    reports: [
      // General Accounts
      { label: "General Accounts", route: null, icon: AccountBalanceWalletIcon, isSection: true },
      { label: "1. Ledger (All in One)", route: "/Ledger", icon: AssuredWorkloadIcon },
      { label: "2. CashBook", route: "/CashBook", icon: MenuBookIcon },
      { label: "3. Income / Expense Report", route: "/IncomeExpenseReport", icon: RequestPageIcon },
      { label: "4. Expense Report", route: "/ExpenseAdvancedReport", icon: DescriptionIcon },
      { label: "5. Detailed Invoice", route: "/DetailedInvoice", icon: DescriptionIcon },
      // Primary Stock Book
      { label: "Primary Stock Book", route: null, icon: InventoryIcon, isSection: true },
      { label: "1. Stock Day Book", route: "/StockDayBook", icon: MenuBookIcon },
      { label: "2. Stock Ledger", route: "/StockLedger", icon: AssuredWorkloadIcon },
      { label: "3. Daily Item Movement", route: "/DailyItemMovement", icon: TimelineIcon },
      { label: "4. Monthly Item Movement", route: "/MonthlyItemMovement", icon: BarChartIcon },
      // Stock Trail
      { label: "Stock Trail", route: null, icon: TimelineIcon, isSection: true },
      { label: "1. Stock Fast / Slow Moving Item", route: "/FastSlowMovingItems", icon: TimelineIcon },
      { label: "2. Stock Trail Transaction Wise", route: "/StockTrailTransactionWise", icon: CompareArrowsIcon },
      { label: "3. Stock Trail Month Wise", route: "/StockTrailMonthWise", icon: BarChartIcon },
      // Stock Valuation
      { label: "Stock Valuation", route: null, icon: AssessmentIcon, isSection: true },
      { label: "1. Stock Valuation", route: "/StockValuation", icon: SummarizeIcon },
      // Stock Transfer
      { label: "Stock Transfer", route: null, icon: CompareArrowsIcon, isSection: true },
      { label: "1. Stock Transfer", route: "/StockTransfer", icon: CompareArrowsIcon },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  // 3. ACCOUNT PAYABLE
  //    Purchase Analysis + Purchase Register + Vendor Ageing
  // ─────────────────────────────────────────────────────────────
  {
    label: "Account Payable",
    groupIcon: LocalAtmIcon,
    color: "#0d8392",
    bg: "#e0f7fa",
    reports: [
      // Purchase Analysis
      { label: "Purchase Analysis", route: null, icon: ShoppingCartIcon, isSection: true },
      { label: "1. Purchase Report", route: "/PurchaseReport", icon: SummarizeIcon },
      { label: "2. PO Status Report", route: "/PurchaseOrderStatusReport", icon: ReceiptLongIcon },
      { label: "3. Vendor & Item Wise", route: "/VendorandItemWisePurchaseReport", icon: PersonSearchIcon },
      // { label: "4. Vendor Ageing", route: "/VendorAgeingReport", icon: ReceiptLongIcon },
      { label: "4. Purchase Register", route: "/PurchaseRegister", icon: BarChartIcon },
      { label: "5. Order Analysis", route: "/OrderAnalysisReport", icon: SummarizeIcon },
      { label: "6. Purchase Analysis", route: "/PurchaseAnalysisReport", icon: ReceiptLongIcon },
      { label: "7. Purchase Return Analysis", route: "/PurchaseReturnAnalysisReport", icon: PersonSearchIcon },
      { label: "8. Year On Year Purchase Comparison", route: "/YearOnYearPurchaseReport", icon: ReceiptLongIcon },
      // Purchase Register
      { label: "Purchase Register", route: null, icon: InventoryIcon, isSection: true },
      { label: "1. Purchase Order Wise", route: "/PurchaseOrderWiseReport", icon: MenuBookIcon },
      { label: "2. Purchase Invoice Register", route: "/PurchaseInvoiceRegister", icon: AssuredWorkloadIcon },
      { label: "3. Purchase Summary Bill Wise", route: "/PurchaseSummaryBillWise", icon: SummarizeIcon },
      { label: "4. Price List Purchase", route: "/PurchasePriceList", icon: CompareArrowsIcon },
      // Bills O/S and Ageing
      { label: "Bills O / S and Ageing", route: null, icon: TimelineIcon, isSection: true },
      { label: "1. Payment Remainder (Payable Due)", route: "/PurchasePaymentReminder", icon: SummarizeIcon },
      { label: "2. Bill Wise Outstanding (Payable Due)", route: "/PurchaseBillWiseOutstanding", icon: CompareArrowsIcon },
      { label: "3. Vendor Group Bill Wise Outstanding (Payable Due)", route: "/VendorGroupOutstandingReport", icon: CompareArrowsIcon },
      { label: "4. Vendor Bill Wise Payment Details", route: "/VendorBillWisePaymentDetails", icon: SummarizeIcon },
      { label: "5. Vendor Ageing Analysis Bill To Bill", route: "/VendorAgeingBillToBill", icon: CompareArrowsIcon },
      { label: "6. Vendor Ageing Analysis FIFO Based", route: "/VendorAgeingFIFO", icon: SummarizeIcon },
    ],
  },

  // ─────────────────────────────────────────────────────────────
  // 4. ACCOUNT RECEIVABLE
  //    Sales Analysis + Sales Register + Customer Ageing
  //    + Stock Analysis + Item Profitability
  // ─────────────────────────────────────────────────────────────
  {
    label: "Account Receivable",
    groupIcon: ShowChartIcon,
    color: "#1b5e20",
    bg: "#e8f5e9",
    reports: [
      // Sales Analysis
      { label: "Sales Analysis", route: null, icon: TrendingUpIcon, isSection: true },
      { label: "1. Sales Report", route: "/SalesReport", icon: SummarizeIcon },
      { label: "2. SO Status Report", route: "/SalesOrderReport", icon: ReceiptLongIcon },
      { label: "3. Customer & Item Wise", route: "/CustomerWiseSalesReport", icon: PeopleAltIcon },
      // { label: "4. Customer Ageing", route: "/CustomerAgeingReport", icon: ReceiptLongIcon },
      { label: "4. Sales Register", route: "/SalesRegister", icon: BarChartIcon },
      { label: "5. Sales Analysis", route: "/SalesAnalysis", icon: ReceiptLongIcon },
      { label: "6. Despatch Analysis", route: "/DespatchAnalysis", icon: PeopleAltIcon },
      { label: "7. Sales Return Analysis", route: "/SaleReturnAnalysis", icon: ReceiptLongIcon },
      { label: "8. Year On Year Sales Comparison", route: "/YearOnYearSales", icon: BarChartIcon },
      // Sales Reports
      { label: "Sales Reports", route: null, icon: InventoryIcon, isSection: true },
      { label: "1. Sales Invoice Register", route: "/SaleInvoiceRegister", icon: MenuBookIcon },
      { label: "2. Sale Summary Bill Wise", route: "/SaleSummaryBillWise", icon: AssuredWorkloadIcon },
      { label: "3. Price List Sales", route: "/SalePriceList", icon: SummarizeIcon },
      { label: "4. Staff Sale Report", route: "/StaffSaleReport", icon: SummarizeIcon },
      // Bills O/S and Ageing
      { label: "Bills O / S and Ageing", route: null, icon: TimelineIcon, isSection: true },
      { label: "1. Payment Remainder (Outstanding Receivable)", route: "/PaymentRemainder", icon: SummarizeIcon },
      { label: "2. Bill Wise Outstanding (Outstanding Receivable)", route: "/BillWiseOutstanding", icon: CompareArrowsIcon },
      { label: "3. Client Group Outstanding (Outstanding Receivable)", route: "/ClientGroupOutstandingReport", icon: CompareArrowsIcon },
      { label: "4. Client Bill Wise Payment Details", route: "/ClientBillWisePaymentDetails", icon: SummarizeIcon },
      { label: "5. Client Ageing Analysis Bill To Bill", route: "/ClientAgeingAnalysisBillWise", icon: CompareArrowsIcon },
      { label: "6. Client Ageing Analysis FIFO Based", route: "/ClientAgeingFIFO", icon: SummarizeIcon },
      // Stock Analysis
      { label: "Stock Analysis", route: null, icon: InventoryIcon, isSection: true },
      { label: "1. Stock Transfer Report", route: "/StockTransferReport", icon: CompareArrowsIcon },
      { label: "2. Stock Movement Report", route: "/StockMovementReport", icon: TimelineIcon },
      { label: "3. Stock Summary", route: "/StockSummaryReport", icon: TimelineIcon },
      { label: "4. Stock In - Out Analysis", route: "/StockAnalysisReport", icon: CompareArrowsIcon },
      { label: "5. Stock Ageing", route: "/StockAgeingReport", icon: TimelineIcon },
      { label: "6. Rate Analysis", route: "/RateAnalysisReport", icon: TimelineIcon },
      { label: "7. Production Wise Stock", route: "/ProductionWiseStockReport", icon: TimelineIcon },
      // Item Profitability
      { label: "Item Profitability", route: null, icon: PieChartIcon, isSection: true },
      { label: "1. Stock Based", route: "/StockBasedReport", icon: CompareArrowsIcon },
      { label: "2. Sales Based", route: "/SalesBasedReport", icon: TimelineIcon },
      { label: "3. Batch Wise (Sales Based)", route: "/BatchBasedReport", icon: TimelineIcon },
      { label: "4. Party Wise (Sales Based)", route: "/PartyWiseReport", icon: TimelineIcon },
      { label: "5. SalesMan Wise (Sales Based)", route: "/SalesManWiseReport", icon: CompareArrowsIcon },
      { label: "6. Stock Minimal Level", route: "/StockMinimalLevelReport", icon: TimelineIcon },
      { label: "7. Item Movement Analysis", route: "/ItemMovementAnalysisReport", icon: TimelineIcon },
    ],
  },

];

const ReportPanel = ({ group, history, onCountClick, searchTerm, favorites, onToggleFavorite }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const GroupIcon = group.groupIcon;

  // Filter reports based on search term
  const term = searchTerm?.toLowerCase() || "";
  const filteredReports = term
    ? group.reports.filter((r) => {
      if (r.isSection) return false;
      return r.label.toLowerCase().includes(term);
    })
    : group.reports;

  // If searching and no matches in this group, don't render
  if (term && filteredReports.length === 0) return null;

  const displayCount = term
    ? filteredReports.length
    : group.reports.filter((r) => !r.isSection).length;

  return (
    <Box
      sx={{
        border: `1px solid ${group.color}44`,
        borderRadius: "4px",
        overflow: "hidden",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: group.color,
          px: "10px",
          py: "5px",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          flexShrink: 0,
        }}
      >
        <GroupIcon sx={{ fontSize: 13, color: "#fff" }} />
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            color: "#fff",
            flex: 1,
            letterSpacing: "0.4px",
            textTransform: "uppercase",
          }}
        >
          {group.label}
        </Typography>
        <Box
          onClick={(e) => { e.stopPropagation(); onCountClick(group); }}
          sx={{
            background: "rgba(255,255,255,0.25)",
            borderRadius: "8px",
            px: "5px",
            py: "1px",
            cursor: "pointer",
            "&:hover": { background: "rgba(255,255,255,0.45)" },
            transition: "background 0.15s",
          }}
        >
          <Typography sx={{ fontSize: "9px", fontWeight: 700, color: "#fff" }}>
            {displayCount}
          </Typography>
        </Box>
      </Box>

      {/* Full list - no scroll, full height */}
      <Box
        sx={{
          overflowY: "visible",
        }}
      >
        {filteredReports.map((report, idx) => {
          const isHov = hoveredIdx === idx;
          const isFav = report.route && favorites.includes(report.route);

          // Section header row
          if (report.isSection) {
            return (
              <Box
                key={report.label}
                sx={{
                  px: "10px",
                  py: "3px",
                  background: "#f0f0f0",
                  borderBottom: "1px solid #e0e0e0",
                  borderTop: idx > 0 ? "1px solid #e0e0e0" : "none",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: group.color,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {report.label}
                </Typography>
              </Box>
            );
          }

          return (
            <Box
              key={report.label}
              onClick={() => history.push(report.route)}
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "7px",
                px: "10px",
                py: { xs: "8px", sm: "4px" },
                cursor: "pointer",
                background: isHov ? group.bg : "transparent",
                borderBottom: "1px solid #f5f5f5",
                borderLeft: isHov ? `3px solid ${group.color}` : "3px solid transparent",
                transition: "all 0.1s",
                userSelect: "none",
                minHeight: { xs: "36px", sm: "auto" },
              }}
            >
              <FiberManualRecordIcon
                sx={{ fontSize: "6px", color: isHov ? group.color : "#bdbdbd", flexShrink: 0, transition: "color 0.1s" }}
              />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: isHov ? 600 : 400,
                  color: isHov ? (report.color || group.color) : (report.color || "#212121"),
                  flex: 1,
                  transition: "all 0.1s",
                  lineHeight: 1.5,
                }}
              >
                {report.label}
              </Typography>
              {/* Favorite icon */}
              <Box
                onClick={(e) => { e.stopPropagation(); onToggleFavorite(report.route); }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  opacity: isFav ? 1 : (isHov ? 0.7 : 0),
                  transition: "opacity 0.15s",
                  flexShrink: 0,
                }}
              >
                {isFav ? (
                  <StarIcon sx={{ fontSize: "14px", color: "#f5a623" }} />
                ) : (
                  <StarBorderIcon sx={{ fontSize: "14px", color: "#999" }} />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const ReportsHub = () => {
  const history = useHistory();
  const { companyId, userId, token } = useAuth();
  const { logAudit } = useAuditLog();
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:960px)");
  const [modalGroup, setModalGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [favorites, setFavorites] = useState([]);
  const [favMap, setFavMap] = useState({}); // route -> Id mapping for delete
  const [favBusy, setFavBusy] = useState(false);

  // Load favorites from API
  const loadFavorites = useCallback(async () => {
    if (!userId || !token) return;
    try {
      const res = await fetchData("FavoriteReportApi", `/${userId}`, token);
      const data = res.response_data || res || [];
      const routes = [];
      const map = {};
      data.forEach((item) => {
        if (item.IsActive && item.Name) {
          routes.push(item.Name);
          map[item.Name] = item.Id;
        }
      });
      setFavorites(routes);
      setFavMap(map);
    } catch (err) {
      console.error("Failed to load favorites:", err);
    }
  }, [userId, token]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const [favMsg, setFavMsg] = useState("");

  const toggleFavorite = async (route) => {
    if (!companyId || !token || !userId) return;
    if (favBusy) return;

    setFavBusy(true);
    try {
      const isFav = favorites.includes(route);

      if (isFav) {
        // Remove favorite
        const id = favMap[route];
        if (id) {
          await deleteData("FavoriteReportApi", id, token);
      logAudit(AUDIT_ACTION.DELETE, 'FavoriteReportApi', id, { Id: id }, null);
        }
        setFavMsg("Removed from favorites");
        setTimeout(() => setFavMsg(""), 2000);
        // Reload from API
        await loadFavorites();
      } else {
        // Check server if already exists
        const res = await fetchData("FavoriteReportApi", `/${userId}`, token);
        const existing = (res.response_data || res || []).find(
          (item) => item.Name === route && item.IsActive
        );
        if (existing) {
          setFavMsg("Already added to favorites");
          setTimeout(() => setFavMsg(""), 2000);
          setFavorites((prev) => prev.includes(route) ? prev : [...prev, route]);
          setFavMap((prev) => ({ ...prev, [route]: existing.Id }));
        } else {
          const payload = {
            CompanyId: companyId,
            UserId: userId,
            Name: route,
            IsActive: true,
            CreatedBy: userId,
            CreatedDate: new Date().toISOString(),
          };
          await postData("FavoriteReportApi", "", payload, token);
      logAudit(AUDIT_ACTION.CREATE, 'FavoriteReportApi', 0, null, { ReportName: payload.ReportName || '' });
          setFavMsg("Added to favorites");
          setTimeout(() => setFavMsg(""), 2000);
          // Reload from API
          await loadFavorites();
        }
      }
    } catch (err) {
      console.error("Failed to toggle favorite:", err);
      setFavMsg("Something went wrong");
      setTimeout(() => setFavMsg(""), 2000);
    }
    setFavBusy(false);
  };

  const cols = isMobile ? 1 : isTablet ? 2 : 4;

  return (
    <Box
      sx={{
        background: "#f0f2f5",
        p: isMobile ? "8px" : "12px",
        minHeight: 0,
      }}
    >
      {/* Search Bar */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "6px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            background: "#fff",
            borderRadius: "16px",
            border: "1px solid #ccc",
            px: "10px",
            py: "2px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            width: isMobile ? "100%" : "200px",
            transition: "box-shadow 0.2s, border-color 0.2s",
            "&:focus-within": {
              borderColor: "#1976d2",
              boxShadow: "0 1px 4px rgba(25,118,210,0.15)",
            },
          }}
        >
          <SearchIcon sx={{ color: "#888", fontSize: 16, mr: "6px" }} />
          <InputBase
            placeholder="Search reports"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, fontSize: "12px" }}
            inputProps={{ "aria-label": "Search reports" }}
          />
          {searchTerm && (
            <Typography
              onClick={() => setSearchTerm("")}
              sx={{ fontSize: "14px", color: "#999", cursor: "pointer", ml: "4px", lineHeight: 1, "&:hover": { color: "#333" } }}
            >
              ×
            </Typography>
          )}
        </Box>
      </Box>

      {/* Report Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gap: "10px",
          alignItems: "stretch",
          pb: isMobile ? "16px" : "8px",
        }}
      >
        {reportGroups.map((group) => (
          <ReportPanel key={group.label} group={group} history={history} onCountClick={setModalGroup} searchTerm={searchTerm} favorites={favorites} onToggleFavorite={toggleFavorite} />
        ))}
      </Box>

      {/* Reports Modal */}
      {modalGroup && (
        <Box
          onClick={() => setModalGroup(null)}
          sx={{
            position: "fixed", inset: 0, zIndex: 1300,
            background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
            p: "16px",
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              background: "#fff",
              borderRadius: isMobile ? "0" : "10px",
              overflow: "hidden",
              width: "100%",
              maxWidth: isMobile ? "100%" : 380,
              height: isMobile ? "100%" : "auto",
              maxHeight: isMobile ? "100%" : "80vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            {/* Modal Header */}
            <Box sx={{ background: modalGroup.color, px: "14px", py: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
              <Box component={modalGroup.groupIcon} sx={{ fontSize: 15, color: "#fff" }} />
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#fff", flex: 1, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                {modalGroup.label}
              </Typography>
              <Typography
                onClick={() => setModalGroup(null)}
                sx={{ fontSize: "18px", color: "rgba(255,255,255,0.8)", cursor: "pointer", lineHeight: 1, "&:hover": { color: "#fff" } }}
              >
                ×
              </Typography>
            </Box>

            {/* Modal Items */}
            <Box sx={{ overflowY: "auto", flex: 1 }}>
              {modalGroup.reports.map((report, idx) => {
                if (report.isSection) {
                  return (
                    <Box key={report.label} sx={{ px: "14px", py: "6px", background: "#f0f0f0", borderBottom: "1px solid #e0e0e0", borderTop: idx > 0 ? "1px solid #e0e0e0" : "none" }}>
                      <Typography sx={{ fontSize: "10px", fontWeight: 700, color: modalGroup.color, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        {report.label}
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <Box
                    key={report.label}
                    onClick={() => { history.push(report.route); setModalGroup(null); }}
                    sx={{
                      display: "flex", alignItems: "center", gap: "10px",
                      px: "14px", py: isMobile ? "12px" : "10px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f5f5f5",
                      borderLeft: `3px solid transparent`,
                      "&:hover": { background: modalGroup.bg, borderLeft: `3px solid ${modalGroup.color}` },
                      "&:active": { background: modalGroup.bg, borderLeft: `3px solid ${modalGroup.color}` },
                      transition: "all 0.1s",
                      minHeight: isMobile ? "44px" : "auto",
                    }}
                  >
                    <FiberManualRecordIcon sx={{ fontSize: "7px", color: modalGroup.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "13px", color: report.color || "#212121", flex: 1 }}>
                      {report.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}

      {/* Favorite Toast Message */}
      {favMsg && (
        <Box
          sx={{
            position: "fixed",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#333",
            color: "#fff",
            px: "16px",
            py: "8px",
            borderRadius: "8px",
            fontSize: "12px",
            fontWeight: 500,
            zIndex: 1400,
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            animation: "fadeInUp 0.2s ease",
          }}
        >
          {favMsg}
        </Box>
      )}
    </Box>
  );
};

export default ReportsHub;

